import type { ReactNode } from 'react';
import type { Project } from '@mockd/shared';
import { ProjectList } from './ProjectList';
import { LimitIndicator } from '../common';

interface ProjectsSectionProps {
  title?: string;
  /** `h1` when the section is the page (Dashboard); `h2` under a page title (TeamDashboard). */
  headingLevel?: 'h1' | 'h2';
  projects: Project[];
  endpointCounts?: Record<string, number>;
  endpointLimit?: number;
  canCreate: boolean;
  onCreateClick: () => void;
  isCreating?: boolean;
  limit?: { used: number; max: number; label: string };
  // Extra header controls rendered after the Create button (e.g. Dashboard's
  // Instant Mock dropdown, which has no team-project equivalent).
  headerActions?: ReactNode;
}

// Shared personal/team project list view (issue #158: "personal vs team
// project list share one view parameterized by context"). Dashboard and
// TeamDashboard both render this with their own fetched projects.
export function ProjectsSection({
  title = 'Projects',
  headingLevel = 'h2',
  projects,
  endpointCounts,
  endpointLimit,
  canCreate,
  onCreateClick,
  isCreating,
  limit,
  headerActions,
}: ProjectsSectionProps) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        {headingLevel === 'h1' ? (
          <h1>{title}</h1>
        ) : (
          <h2 className="text-sm font-medium text-base-content/70">{title}</h2>
        )}
        <div className="flex items-center gap-3">
          {limit && (
            <div className="w-36">
              <LimitIndicator used={limit.used} max={limit.max} label={limit.label} />
            </div>
          )}
          {canCreate && (
            <div className="join">
              <button
                onClick={onCreateClick}
                disabled={isCreating}
                className="btn btn-primary btn-sm join-item"
              >
                Create Project
              </button>
              {headerActions}
            </div>
          )}
        </div>
      </div>
      <ProjectList projects={projects} endpointCounts={endpointCounts} endpointLimit={endpointLimit} />
    </>
  );
}
